import {
  BellIcon,
  CaretRightIcon,
  CreditCardIcon,
  EnvelopeIcon,
  HeadsetIcon,
  LockIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import avatar from '@/assets/images/avatar.webp';
import { UserAvatar } from '@/components/common/user-avatar';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import type { User } from '@/features/auth';
import { deviceStats, useDevices } from '@/features/devices';
import { useOpenTicketCount, useSupportStore } from '@/features/support';

import styles from './profile-menu.module.scss';

export type ProfileMenuProps = {
  user: User;
  /** Dismiss the popover (every action closes it first). */
  onClose: () => void;
  /** Opens the Notification Settings drawer (Figma 2389-740152). */
  onOpenNotificationSettings: () => void;
  onSignOut: () => void;
};

/**
 * Rich account popover for the top-nav avatar (Figma 2327-693027): profile card,
 * live fleet stats, the Customer Support / Manage Subscriptions shortcut cards, and a
 * menu list ending in a destructive Logout. Layout chrome — legitimately reads the
 * auth / devices / support feature surfaces.
 */
export function ProfileMenu({
  user,
  onClose,
  onOpenNotificationSettings,
  onSignOut,
}: ProfileMenuProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: devices } = useDevices();
  const { data: openTickets } = useOpenTicketCount();
  const openSupport = useSupportStore((s) => s.openList);

  const stats = deviceStats(devices ?? []);
  const displayName = `${user.firstName} ${user.lastName}`.trim();

  const go = (to: string) => () => {
    onClose();
    navigate(to);
  };

  return (
    <div className={styles.menu} role="menu" aria-label="Account menu">
      <section className={styles.profile}>
        <div className={styles.identity}>
          <UserAvatar name={displayName} src={avatar} size={42} />
          <span className={styles.name}>{displayName}</span>
        </div>
        <div className={styles.contact}>
          {user.email ? (
            <span className={styles.contactRow}>
              <EnvelopeIcon size={16} weight="regular" />
              {user.email}
            </span>
          ) : null}
          {user.phone ? (
            <span className={styles.contactRow}>
              <PhoneIcon size={16} weight="regular" />
              {user.phone}
            </span>
          ) : null}
        </div>
      </section>

      <section className={styles.stats} aria-label="Fleet summary">
        <Stat value={stats.total} label="Devices" />
        <span className={styles.statDivider} aria-hidden />
        <Stat value={stats.active} label="Active" />
        <span className={styles.statDivider} aria-hidden />
        <Stat value={stats.running} label="Running" />
      </section>

      <section className={styles.cards}>
        <button
          type="button"
          className={styles.greenCard}
          onClick={() => {
            onClose();
            openSupport();
          }}
        >
          {openTickets && openTickets > 0 ? (
            <span className={styles.badge}>
              {openTickets} Ticket{openTickets === 1 ? '' : 's'} Active
            </span>
          ) : null}
          <HeadsetIcon size={20} weight="regular" />
          <span className={styles.cardLabel}>Customer Support</span>
        </button>
        <button type="button" className={styles.greenCard} onClick={go(routes.subscriptions)}>
          <CreditCardIcon size={20} weight="regular" />
          <span className={styles.cardLabel}>Manage Subscriptions</span>
        </button>
      </section>

      <section className={styles.list}>
        <button
          type="button"
          className={styles.item}
          role="menuitem"
          onClick={() => {
            onClose();
            onOpenNotificationSettings();
          }}
        >
          <BellIcon size={20} weight="regular" />
          <span className={styles.itemLabel}>Notification Preferences</span>
          <CaretRightIcon size={18} className={styles.chevron} />
        </button>

        <button
          type="button"
          className={styles.item}
          role="menuitem"
          onClick={() => {
            onClose();
            toast.success({
              title: 'Password reset link sent',
              description: user.email
                ? `Check ${user.email} to finish resetting your password.`
                : 'Check your email to finish resetting your password.',
            });
          }}
        >
          <LockIcon size={20} weight="regular" />
          <span className={styles.itemLabel}>Reset Password</span>
          <CaretRightIcon size={18} className={styles.chevron} />
        </button>

        <button type="button" className={styles.item} role="menuitem" onClick={go(routes.warranty)}>
          <ShieldCheckIcon size={20} weight="regular" />
          <span className={styles.itemLabel}>Warranty Claims</span>
          <CaretRightIcon size={18} className={styles.chevron} />
        </button>

        <button
          type="button"
          className={`${styles.item} ${styles.danger}`}
          role="menuitem"
          onClick={() => {
            onClose();
            onSignOut();
          }}
        >
          <SignOutIcon size={20} weight="regular" />
          <span className={styles.itemLabel}>Logout</span>
        </button>
      </section>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <span className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </span>
  );
}
